import type { CraneConfig } from '../config/minigames.config';
import { AttemptGrade, MiniGameKind, gradeOf, gradedShare, type MiniGame, type MiniGameAttempt } from './MiniGame';

/** Gravity of the swing (m/s²) – the pendulum moves like a real one: 5 m of rope swing once in ~4.5 s. */
const SWING_GRAVITY = 9.81;
const DEG = Math.PI / 180;

export interface LandedLoad {
  /** Where it came down, relative to the target centre (m). */
  readonly x: number;
  readonly grade: AttemptGrade;
}

/**
 * Crane (user 2026-10-04: "langsam pendeln … maximal zehn bis zwanzig Grad"): the trolley stands still, the pallet
 * swings calmly on the rope beneath it. The mark lies beside the point under the trolley; a tap releases the hook and
 * the load flies on with the speed of its swing – the moment has to be right. Three loads; between them the trolley
 * moves calmly to the next spot. Dead centre counts in full, on the marker off centre a little, beside it nothing.
 */
export class CraneGame implements MiniGame {
  readonly kind = MiniGameKind.Crane;
  readonly attemptsTotal: number;
  readonly landed: LandedLoad[] = [];
  private readonly amplitude: number;
  private readonly omega: number;
  private swingTime = 0;
  private phase0: number;
  /** Trolley position now, and where it travels to after a landing. */
  private trolley: number;
  private trolleyFrom: number;
  private trolleyTo: number;
  private fall: { x: number; y: number; vx: number; vy: number } | null = null;
  /** Seconds since the last load came down (the next one hangs on the hook after settleSeconds), null otherwise. */
  private settling: number | null = null;
  private attemptTime = 0;
  private shareNow = 0;
  private lastGrade: AttemptGrade | null = null;
  private sinceGrade = Infinity;

  constructor(
    private readonly config: CraneConfig,
    round: number,
    private readonly random: () => number,
  ) {
    this.attemptsTotal = config.loads;
    this.amplitude = config.swingDegrees[Math.min(round, config.swingDegrees.length - 1)]! * DEG;
    this.omega = Math.sqrt(SWING_GRAVITY / config.ropeLength);
    this.phase0 = random() * Math.PI * 2;
    this.trolley = this.trolleyFrom = this.trolleyTo = this.nextTrolley();
  }

  /** Height of the trolley over the slab (m). */
  get trolleyHeight(): number {
    return this.config.dropHeight + this.config.ropeLength;
  }

  /** Trolley along the jib (m, the mark at 0). */
  get trolleyX(): number {
    return this.trolley;
  }

  /** Swing angle of the rope (rad, 0 = straight down). */
  get swing(): number {
    return this.settling !== null || this.fall ? 0 : this.amplitude * Math.sin(this.omega * this.swingTime + this.phase0);
  }

  /** How far a release can carry the load from under the trolley (m): its swing plus its flight. */
  get reach(): number {
    const c = this.config;
    const flight = Math.sqrt((2 * c.dropHeight) / c.gravity);
    return Math.hypot(c.ropeLength * Math.sin(this.amplitude), c.ropeLength * this.amplitude * this.omega * flight);
  }

  /** The load's position (bottom centre, m over the slab): on the hook, falling, or null while the next one comes. */
  get load(): { readonly x: number; readonly y: number; readonly hanging: boolean } | null {
    if (this.fall) return { x: this.fall.x, y: this.fall.y, hanging: false };
    if (this.settling !== null || this.finished) return null;
    const c = this.config;
    const theta = this.swing;
    return { x: this.trolley + c.ropeLength * Math.sin(theta), y: this.trolleyHeight - c.ropeLength * Math.cos(theta), hanging: true };
  }

  get share(): number {
    return this.shareNow;
  }

  get grade(): AttemptGrade | null {
    return this.lastGrade;
  }

  get secondsSinceGrade(): number {
    return this.sinceGrade;
  }

  get attemptsMade(): number {
    return this.landed.length;
  }

  get finished(): boolean {
    return this.landed.length >= this.config.loads && this.fall === null && this.settling === null;
  }

  get timeShare(): number {
    return this.fall || this.settling !== null ? 1 : Math.max(0, 1 - this.attemptTime / this.config.secondsPerLoad);
  }

  tap(): void {
    const hanging = this.load;
    if (!hanging?.hanging) return;
    const c = this.config;
    const arg = this.omega * this.swingTime + this.phase0;
    const theta = this.amplitude * Math.sin(arg);
    const thetaDot = this.amplitude * this.omega * Math.cos(arg);
    this.fall = {
      x: hanging.x,
      y: hanging.y,
      vx: c.ropeLength * Math.cos(theta) * thetaDot,
      vy: c.ropeLength * Math.sin(theta) * thetaDot,
    };
  }

  update(dt: number): MiniGameAttempt | null {
    this.sinceGrade += dt;
    if (this.finished) return null;
    if (this.fall) return this.updateFall(dt);
    if (this.settling !== null) {
      this.settling += dt;
      const k = Math.min(1, this.settling / this.config.settleSeconds);
      this.trolley = this.trolleyFrom + (this.trolleyTo - this.trolleyFrom) * (k * k * (3 - 2 * k));
      if (k >= 1) {
        this.settling = null;
        this.attemptTime = 0;
        this.swingTime = 0;
        this.phase0 = this.random() * Math.PI * 2;
      }
      return null;
    }
    this.swingTime += dt;
    this.attemptTime += dt;
    // too late: the load is put down beside the slab
    if (this.attemptTime >= this.config.secondsPerLoad) return this.judge(Infinity);
    return null;
  }

  /** Trolley spot for the next load: the mark beside it, left or right, a share of the reach away. */
  private nextTrolley(): number {
    const o = this.config.targetOffset;
    const side = this.random() < 0.5 ? -1 : 1;
    return -side * (o.min + this.random() * (o.max - o.min)) * this.reach;
  }

  private updateFall(dt: number): MiniGameAttempt | null {
    const f = this.fall!;
    f.vy -= this.config.gravity * dt;
    f.x += f.vx * dt;
    f.y += f.vy * dt;
    if (f.y > 0) return null;
    this.fall = null;
    return this.judge(Math.abs(f.x), f.x);
  }

  private judge(distance: number, x = Infinity): MiniGameAttempt {
    const c = this.config;
    const grade = gradeOf(distance, c.perfectHalfWidth, c.targetHalfWidth);
    const gained = gradedShare(distance, c.perfectHalfWidth, c.targetHalfWidth, c.onTarget.near, c.onTarget.edge) / c.loads;
    this.shareNow = Math.min(1, this.shareNow + gained);
    this.landed.push({ x, grade });
    this.lastGrade = grade;
    this.sinceGrade = 0;
    this.settling = 0;
    this.trolleyFrom = this.trolley;
    if (this.landed.length < c.loads) this.trolleyTo = this.nextTrolley();
    return { grade, gained };
  }
}
